import { ASK_USER_FREE_TEXT_PLACEHOLDER } from '@littlebird/sdk';

import { Icons } from '@/assets/icons/icons';
import { XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

// The rows of an ask_user question, shared by the in-chat picker and the question card

const Badge = ({
  children,
  isSelected = false,
}: {
  children: React.ReactNode;
  isSelected?: boolean;
}) => (
  <span
    className={cn(
      'flex size-7 shrink-0 items-center justify-center rounded-md text-sm border border-border',
      isSelected
        ? 'bg-surface-hover text-foreground'
        : 'bg-surface-hover text-foreground-muted'
    )}
  >
    {children}
  </span>
);

export const RowDivider = () => (
  <div className="mx-2.5 h-px bg-border transition-opacity [.ask-user-option-selected+&]:opacity-0 [.ask-user-option:hover+&]:opacity-0 [.ask-user-option:focus+&]:opacity-0 [&:has(+.ask-user-option-selected)]:opacity-0 [&:has(+.ask-user-option:hover)]:opacity-0 [&:has(+.ask-user-option:focus)]:opacity-0" />
);

const CheckboxIndicator = ({ checked }: { checked: boolean }) => (
  <span
    className={cn(
      'flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors',
      checked
        ? 'bg-accent text-accent-foreground border-accent'
        : 'border-border text-transparent'
    )}
  >
    <Icons.check className="size-4" />
  </span>
);

export const OptionRow = ({
  number,
  label,
  isSelected,
  multiSelect,
  disabled = false,
  onClick,
}: {
  number: number;
  label: string;
  isSelected: boolean;
  multiSelect: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'ask-user-option flex w-full items-center gap-3 rounded-xl px-2.5 text-left transition-colors focus:bg-surface-hover focus:outline-none disabled:cursor-default',
      !multiSelect && isSelected
        ? 'ask-user-option-selected bg-surface-active'
        : 'hover:bg-surface-hover disabled:hover:bg-transparent',
      multiSelect ? 'py-3' : 'py-2'
    )}
  >
    {multiSelect ? (
      <CheckboxIndicator checked={isSelected} />
    ) : (
      <Badge isSelected={isSelected}>{number}</Badge>
    )}
    <span className="text-foreground min-w-0 flex-1 truncate text-sm">
      {label}
    </span>
    {!multiSelect && isSelected && (
      <Icons.navigateRight className="text-foreground-muted size-4 shrink-0" />
    )}
  </button>
);

// The line for an answer in the user's own words: a pencil when one answer is
// allowed, since typing replaces the pick, or a checkbox beside the picks
export const FreeTextRow = ({
  value,
  multiSelect,
  disabled = false,
  placeholder = ASK_USER_FREE_TEXT_PLACEHOLDER,
  onChange,
  onFocus,
  onBlur,
  children,
}: {
  value: string;
  multiSelect: boolean;
  disabled?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  children?: React.ReactNode;
}) => (
  <XStack className="items-center gap-2 pr-2">
    <label
      className={cn(
        'flex min-w-0 flex-1 items-center gap-3 px-2.5',
        multiSelect ? 'pt-3 pb-2' : 'py-2'
      )}
    >
      {multiSelect ? (
        <CheckboxIndicator checked={Boolean(value)} />
      ) : (
        <Badge>
          <Icons.pencil className="size-3.5" />
        </Badge>
      )}
      <input
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        className="text-foreground placeholder:text-foreground-muted min-w-0 flex-1 border-0 bg-transparent p-0 text-sm focus:outline-none"
      />
    </label>
    {children}
  </XStack>
);
